import { useMemo } from 'react';
import type { MartVerificationRow } from '../types/operations.types';
import { formatNumber } from '../helpers/etl-health.helpers';
import { EtlDetailTable } from './etl-detail-table';
import { EtlHealthStatusPill } from './etl-health-status-pill';

type Props = {
  rows: MartVerificationRow[];
  selectedDate: string | null;
};

export function MartVerificationPanel({ rows, selectedDate }: Props) {
  const filteredRows = useMemo(() => {
    if (!selectedDate) {
      return rows;
    }
    return rows.filter((row) => row.date === selectedDate);
  }, [rows, selectedDate]);

  const datasourceOptions = useMemo(
    () => [...new Set(rows.map((row) => row.datasource))].sort(),
    [rows],
  );
  const connectorOptions = useMemo(
    () => [...new Set(rows.map((row) => row.connector))].sort(),
    [rows],
  );
  const statusOptions = useMemo(
    () => [...new Set(rows.map((row) => row.status))].sort(),
    [rows],
  );

  return (
    <EtlDetailTable
      rows={filteredRows}
      columns={[
        { id: 'date', header: 'Date', accessor: (row) => row.date, sortable: true },
        { id: 'datasource', header: 'Datasource', accessor: (row) => row.datasource, sortable: true },
        { id: 'connector', header: 'Connector', accessor: (row) => row.connector, sortable: true },
        { id: 'check_type', header: 'Check type', accessor: (row) => row.check_type, sortable: true },
        {
          id: 'value',
          header: 'Value',
          accessor: (row) => row.value,
          sortable: true,
          align: 'right',
          render: (row) => formatNumber(row.value),
        },
        {
          id: 'threshold',
          header: 'Threshold',
          accessor: (row) => row.threshold,
          sortable: true,
          align: 'right',
          render: (row) => formatNumber(row.threshold),
        },
        {
          id: 'status',
          header: 'Status',
          accessor: (row) => row.status,
          sortable: true,
          render: (row) => <EtlHealthStatusPill status={row.status} />,
        },
        { id: 'ingestion_ts', header: 'Ingestion ts', accessor: (row) => row.ingestion_ts, sortable: true },
      ]}
      filters={[
        {
          id: 'datasource',
          label: 'Datasource',
          options: datasourceOptions,
          getValue: (row) => row.datasource,
        },
        {
          id: 'connector',
          label: 'Connector',
          options: connectorOptions,
          getValue: (row) => row.connector,
        },
        {
          id: 'status',
          label: 'Status',
          options: statusOptions,
          getValue: (row) => row.status,
        },
      ]}
      emptyMessage="No mart verification checks for the selected period."
    />
  );
}
